import React, { useState, useRef } from 'react';
import { 
  Calendar, 
  FileText, 
  Plus, 
  X, 
  Save, 
  Printer,
  User,
  Search,
  ChevronDown,
  Check,
  ClipboardList,
  CheckCircle,
  XCircle
} from 'lucide-react';

const Examination = ({ onBack }) => {
  // Sample patient data
  const [patients] = useState([
    { id: 1, code: 'PT001', name: 'Rajesh Kumar', phone: '9876543210' },
    { id: 2, code: 'PT002', name: 'Priya Sharma', phone: '8765432109' },
    { id: 3, code: 'PT003', name: 'Amit Verma', phone: '7654321098' },
    { id: 4, code: 'PT004', name: 'Sneha Patel', phone: '6543210987' },
    { id: 5, code: 'PT005', name: 'Vikram Singh', phone: '9432109876' }
  ]);

  // Sample tests data
  const [availableTests] = useState([
    { id: 1, name: 'Complete Blood Count (CBC)', mrp: 500, centerPrice: 400 },
    { id: 2, name: 'Blood Glucose Fasting', mrp: 300, centerPrice: 250 },
    { id: 3, name: 'Lipid Profile', mrp: 800, centerPrice: 650 },
    { id: 4, name: 'Liver Function Test', mrp: 900, centerPrice: 750 },
    { id: 5, name: 'Thyroid Profile', mrp: 1000, centerPrice: 850 },
    { id: 6, name: 'Urine Routine Examination', mrp: 200, centerPrice: 150 },
    { id: 7, name: 'ECG', mrp: 400, centerPrice: 350 },
    { id: 8, name: 'X-Ray Chest', mrp: 600, centerPrice: 500 }
  ]);

  // Form state
  const [formData, setFormData] = useState({
    patientId: '',
    scheduleDate: '',
    voucherNo: '',
    selectedTests: []
  });

  const [patientSearch, setPatientSearch] = useState('');
  const [testSearch, setTestSearch] = useState('');
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const printRef = useRef();

  // Filter patients based on search term
  const filteredPatients = patients.filter(patient =>
    patient.name.toLowerCase().includes(patientSearch.toLowerCase()) ||
    patient.code.toLowerCase().includes(patientSearch.toLowerCase()) ||
    patient.phone.includes(patientSearch)
  );

  // Filter tests based on search term
  const filteredTests = availableTests.filter(test =>
    test.name.toLowerCase().includes(testSearch.toLowerCase())
  );

  // Calculate total amount
  const totalAmount = formData.selectedTests.reduce((total, test) => total + test.centerPrice, 0);

  // Show toast notification
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: '' });
    }, 3000);
  };

  // Handle patient selection
  const handleSelectPatient = (patient) => {
    setFormData(prev => ({
      ...prev,
      patientId: patient.id
    }));
    setPatientSearch(`${patient.code} - ${patient.name}`);
    setShowPatientDropdown(false);
  };

  // Handle test selection
  const handleAddTest = (test) => {
    if (!formData.selectedTests.some(t => t.id === test.id)) {
      setFormData(prev => ({
        ...prev,
        selectedTests: [...prev.selectedTests, test]
      }));
      showToast(`${test.name} added to examination list`, 'success');
    } else {
      showToast(`${test.name} is already in the examination list`, 'error');
    }
  };

  // Handle test removal
  const handleRemoveTest = (testId) => {
    const testToRemove = formData.selectedTests.find(test => test.id === testId);
    setFormData(prev => ({
      ...prev,
      selectedTests: prev.selectedTests.filter(test => test.id !== testId)
    }));
    showToast(`${testToRemove.name} removed from examination list`, 'error');
  };

  // Reset form function
  const resetForm = () => {
    setFormData({
      patientId: '',
      scheduleDate: '',
      voucherNo: '',
      selectedTests: []
    });
    setPatientSearch('');
    setTestSearch('');
    setShowPrintDialog(false);
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    // Validate form
    if (!formData.patientId || !formData.scheduleDate || formData.selectedTests.length === 0) {
      showToast('Please fill all required fields and select at least one test', 'error');
      return;
    }
    
    // Show print dialog
    setShowPrintDialog(true);
  };

  // Handle print
  const handlePrint = () => {
    const printContent = printRef.current.innerHTML;
    const originalContent = document.body.innerHTML;
    
    document.body.innerHTML = printContent;
    window.print();
    document.body.innerHTML = originalContent;
    
    // Clear form and show success message
    resetForm();
    showToast('Examination saved successfully!', 'success');
  };

  // Get selected patient details
  const selectedPatient = patients.find(p => p.id === parseInt(formData.patientId));

  return (
    <div className="bg-white rounded-lg shadow-md relative">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center space-x-2 transition-all duration-300 ${
          toast.type === 'success' 
            ? 'bg-green-100 text-green-800 border border-green-200' 
            : 'bg-red-100 text-red-800 border border-red-200'
        }`}>
          {toast.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <XCircle className="w-5 h-5" />
          )}
          <span>{toast.message}</span>
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
              </svg>
            </button>
            <div>
              <h1 className="text-2xl font-bold">Examination</h1>
              <p className="text-blue-100">Create new examination record</p>
            </div>
          </div>
          <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
            <FileText className="w-6 h-6" />
          </div>
        </div>
      </div>

      {/* Examination Form */}
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Patient and Schedule Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Choose Patient <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search patients by name or code..."
                value={patientSearch}
                onChange={(e) => {
                  setPatientSearch(e.target.value);
                  setShowPatientDropdown(true);
                }}
                onFocus={() => setShowPatientDropdown(true)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              
              {showPatientDropdown && filteredPatients.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {filteredPatients.map(patient => (
                    <div
                      key={patient.id}
                      onClick={() => handleSelectPatient(patient)}
                      className="p-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                    >
                      <div className="font-medium">{patient.name}</div>
                      <div className="text-sm text-gray-600">
                        {patient.code} • {patient.phone}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Schedule Date <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="date"
                value={formData.scheduleDate}
                onChange={(e) => setFormData({...formData, scheduleDate: e.target.value})}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Voucher No.
          </label>
          <input
            type="text"
            value={formData.voucherNo}
            onChange={(e) => setFormData({...formData, voucherNo: e.target.value})}
            placeholder="Enter voucher number"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Test Selection */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">
            Test Selection
          </h3>

          {/* Search Tests */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search tests..."
              value={testSearch}
              onChange={(e) => setTestSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Available Tests */}
          <div className="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto p-2 border rounded-lg">
            {filteredTests.length > 0 ? (
              filteredTests.map(test => (
                <div key={test.id} className="flex items-center justify-between p-3 border rounded hover:bg-gray-50">
                  <div className="flex-1">
                    <div className="font-medium">{test.name}</div>
                    <div className="text-sm text-gray-600 flex justify-between">
                      <span>MRP: ₹{test.mrp}</span>
                      <span className="text-green-600 font-medium">Our Price: ₹{test.centerPrice}</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleAddTest(test)}
                    className="ml-4 p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors flex items-center"
                  >
                    <Plus className="w-4 h-4" />
                    <span className="ml-1 text-sm">Add</span>
                  </button>
                </div>
              ))
            ) : (
              <div className="text-center py-4 text-gray-500">
                No tests found matching your search
              </div>
            )}
          </div>

          {/* Selected Tests Box */}
          {formData.selectedTests.length > 0 && (
            <div className="mt-8 border-2 border-dashed border-blue-200 rounded-xl p-5 bg-blue-50">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-blue-800 text-lg flex items-center">
                  <ClipboardList className="w-5 h-5 mr-2" />
                  Selected Tests
                </h4>
                <span className="bg-blue-200 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                  {formData.selectedTests.length} test(s)
                </span>
              </div>
              
              <div className="space-y-3">
                {formData.selectedTests.map(test => (
                  <div key={test.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-100 shadow-sm">
                    <div className="flex-1">
                      <div className="flex items-center">
                        <Check className="w-4 h-4 text-green-500 mr-2" />
                        <div className="font-medium text-gray-800">{test.name}</div>
                      </div>
                      <div className="text-sm text-gray-600 mt-1 ml-6">
                        Price: <span className="font-semibold text-green-600">₹{test.centerPrice}</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveTest(test.id)}
                      className="p-2 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                      title="Remove test"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Total Amount */}
              <div className="mt-5 p-4 bg-white rounded-lg border border-blue-200">
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-gray-800 text-lg">Total Amount:</span>
                  <span className="text-2xl font-bold text-green-700">₹{totalAmount}</span>
                </div>
                <div className="text-sm text-gray-600 mt-2 text-right">
                  Includes all selected tests
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Submit Button */}
        {formData.selectedTests.length > 0 && (
          <div className="flex justify-end pt-6 border-t">
            <button
              type="submit"
              className="bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-3 rounded-lg font-medium hover:from-green-700 hover:to-green-800 transition-all duration-200 flex items-center space-x-2 shadow-lg"
            >
              <Save className="w-5 h-5" />
              <span>Save Examination</span>
            </button>
          </div>
        )}
      </form>

      {/* Print Dialog */}
      {showPrintDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Examination Saved Successfully!
            </h3>
            <p className="text-gray-600 mb-6">
              Do you want to print the receipt now?
            </p>
            <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3">
              <button
                onClick={() => {
                  setShowPrintDialog(false);
                  resetForm();
                  showToast('Examination saved successfully!', 'success');
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                No, Thanks
              </button>
              <button
                onClick={handlePrint}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
              >
                <Printer className="w-4 h-4" />
                <span>Yes, Print Receipt</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Printable Receipt (hidden until printing) */}
      <div className="hidden">
        <div ref={printRef} className="p-8">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold">Bluewell Diagnostic Centre</h1>
            <p className="text-gray-600">Examination Receipt</p>
            <p className="text-gray-500 text-sm">Date: {new Date().toLocaleDateString()}</p>
          </div>
          
          <div className="mb-6 border-b pb-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <strong>Patient:</strong> {selectedPatient?.name}
              </div>
              <div>
                <strong>Patient Code:</strong> {selectedPatient?.code}
              </div>
              <div>
                <strong>Phone:</strong> {selectedPatient?.phone}
              </div>
              <div>
                <strong>Voucher No:</strong> {formData.voucherNo || 'N/A'}
              </div>
            </div>
          </div>
          
          <table className="w-full border-collapse border border-gray-300 mb-6">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 p-2 text-left">Test Name</th>
                <th className="border border-gray-300 p-2 text-right">Price (₹)</th>
              </tr>
            </thead>
            <tbody>
              {formData.selectedTests.map(test => (
                <tr key={test.id}>
                  <td className="border border-gray-300 p-2">{test.name}</td>
                  <td className="border border-gray-300 p-2 text-right">{test.centerPrice}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-gray-50">
                <td className="border border-gray-300 p-2 font-bold">Total Amount</td>
                <td className="border border-gray-300 p-2 text-right font-bold">₹{totalAmount}</td>
              </tr>
            </tfoot>
          </table>
          
          <div className="text-center text-sm text-gray-600 mt-10">
            Thank you for choosing Bluewell Diagnostic Centre
          </div>
          <div className="text-center text-xs text-gray-500 mt-2">
            Generated on: {new Date().toLocaleString()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Examination;